import {
  DocumentTextIcon,
  ExclamationTriangleIcon,
  ScaleIcon,
  ShieldExclamationIcon,
  ClipboardDocumentCheckIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';

interface MetricsStripProps {
  metrics: {
    contentReviewedThisMonth: number;
    hmdaErrorRate: number | null;
    fairLendingRiskLevel: string | null;
    openFindings: number;
    daysToNextExam: number | null;
  };
}

function riskLevelColor(level: string | null): string {
  if (!level) return 'text-gray-900';
  const upper = level.toUpperCase();
  if (upper === 'LOW') return 'text-green-600';
  if (upper === 'MEDIUM') return 'text-amber-500';
  if (upper === 'HIGH') return 'text-red-600';
  return 'text-gray-900';
}

function daysToExamColor(days: number | null): string {
  if (days === null) return 'text-gray-900';
  if (days < 14) return 'text-red-600';
  if (days < 30) return 'text-amber-500';
  return 'text-green-600';
}

export default function MetricsStrip({ metrics }: MetricsStripProps) {
  const cards = [
    {
      label: 'Content Reviewed',
      value: String(metrics.contentReviewedThisMonth),
      icon: DocumentTextIcon,
      valueColor: 'text-gray-900',
    },
    {
      label: 'HMDA Error Rate',
      value:
        metrics.hmdaErrorRate !== null
          ? `${metrics.hmdaErrorRate.toFixed(1)}%`
          : '\u2014',
      icon: ExclamationTriangleIcon,
      valueColor: 'text-gray-900',
    },
    {
      label: 'Fair Lending Risk',
      value: metrics.fairLendingRiskLevel || '\u2014',
      icon: ScaleIcon,
      valueColor: riskLevelColor(metrics.fairLendingRiskLevel),
    },
    {
      label: 'Open Findings',
      value: String(metrics.openFindings),
      icon: ShieldExclamationIcon,
      valueColor: 'text-gray-900',
    },
    {
      label: 'Days to Exam',
      value:
        metrics.daysToNextExam !== null
          ? String(metrics.daysToNextExam)
          : '\u2014',
      icon: ClipboardDocumentCheckIcon,
      valueColor: daysToExamColor(metrics.daysToNextExam),
    },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
      {cards.map((card) => {
        const Icon = card.icon;
        return (
          <div
            key={card.label}
            className="flex flex-col items-center text-center py-2"
          >
            <Icon className="h-5 w-5 text-gray-400 mb-1" />
            <span className={clsx('text-xl font-bold', card.valueColor)}>
              {card.value}
            </span>
            <span className="text-sm text-gray-500">{card.label}</span>
          </div>
        );
      })}
    </div>
  );
}
